import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  APP_NAME,
  BLAZE_NAME,
  BLAZE_UPLOAD_BYTES,
  blazeMonths,
  FOUNDER_FREE_DAYS,
  giftLength,
  STOKE_LEVELS,
  STOKES_PER_BLAZE,
  type BlazeCheckoutKind,
  type BlazeStatusDTO,
} from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { blazeApi } from '@/api/endpoints';
import { ServerIcon } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Campfire, Copy, ExternalLink, Fire, Gift, ImagePlus, Monitor, Palette, Smile, Soundboard, Stoke, Upload, UserCircle, type AppIcon } from '@/components/ui/icons';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { localPrices, onPaddleEvent, openCheckout, yearlySaving, type LocalPrice } from '@/lib/paddle';
import { formatDay } from '@/lib/time';
import { cn, copyText } from '@/lib/utils';
import { useBlaze } from '@/stores/blaze';
import { toast } from '@/stores/ui';
import { SettingsCard } from './SettingsShell';

const PERKS: Array<{ icon: AppIcon; title: string; body: string }> = [
  { icon: Smile, title: 'Emoji anywhere', body: 'Custom emoji from all your servers show for everyone, wherever you use them.' },
  { icon: Soundboard, title: 'Sounds anywhere', body: 'Play any of your servers’ soundboard sounds in any voice channel.' },
  { icon: UserCircle, title: 'Server profiles', body: 'A different avatar, banner and bio in each server.' },
  { icon: Monitor, title: 'HD streaming', body: 'Share your screen at 60 fps (smooth enough for games), and your camera in 1080p.' },
  { icon: Upload, title: 'Bigger uploads', body: `Send files up to ${BLAZE_UPLOAD_BYTES / 1024 / 1024} MB.` },
  { icon: Palette, title: 'Blaze styles', body: 'Extra avatar decorations, profile effects and name styles.' },
  { icon: Fire, title: 'A badge that grows', body: 'Your Blaze badge shows how long you’ve kept Blaze, from your first month to two years.' },
  { icon: Stoke, title: `${STOKES_PER_BLAZE} Stokes`, body: 'Boost two servers (or one twice) to unlock their perks.' },
];

const PLAN_LABEL = { MONTHLY: 'Monthly', YEARLY: 'Yearly' } as const;


/** Settings → ClanFire Blaze: what it is, buying it, Founder months, gifts and Stokes. */
export function BlazeSection() {
  const { status, refresh, set } = useBlaze();
  const [prices, setPrices] = useState<Partial<Record<BlazeCheckoutKind, LocalPrice>>>({});
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Local prices from Paddle (in the viewer's currency, with tax), once Blaze is on sale.
  const checkout = status?.checkout ?? null;
  useEffect(() => {
    if (!checkout) return;
    let cancelled = false;
    localPrices(checkout)
      .then((p) => !cancelled && setPrices(p))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [checkout]);

  // After paying, Paddle tells the server (a webhook): check a few times until it has.
  useEffect(
    () =>
      onPaddleEvent((event) => {
        if (event.name !== 'checkout.completed') return;
        toast.success('Payment received. Thank you! Setting things up…');
        let tries = 0;
        const timer = setInterval(() => {
          tries++;
          void refresh();
          if (tries >= 10) clearInterval(timer);
        }, 2_000);
      }),
    [refresh],
  );

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    try {
      await action();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const buy = (kind: BlazeCheckoutKind) =>
    run(kind, async () => {
      if (!checkout) return;
      const { transactionId } = await blazeApi.checkout(kind);
      await openCheckout(checkout, transactionId).catch(() => {
        throw new Error('The checkout couldn’t load. Check your connection (or turn off a blocker for paddle.com) and try again.');
      });
    });

  if (!status) {
    return (
      <div className="flex justify-center py-16 text-fg-muted">
        <Spinner />
      </div>
    );
  }

  const active = !!status.until;
  return (
    <div>
      <Hero status={status} />

      {active ? (
        <MyBlaze status={status} busy={busy} run={run} onStatus={set} />
      ) : (
        <Plans status={status} prices={prices} busy={busy} onBuy={buy} />
      )}

      {status.founder.isFounder && (
        <FounderCard status={status} busy={busy === 'founder'} onClaim={() => run('founder', async () => {
          set(await blazeApi.claimFounder());
          toast.success(`Enjoy your ${FOUNDER_FREE_DAYS / 30} months of ${BLAZE_NAME}!`);
        })} />
      )}

      <SettingsCard title="What you get">
        <ul className="grid gap-3 sm:grid-cols-2">
          {PERKS.map(({ icon: Icon, title, body }) => (
            <li key={title} className="flex gap-3 rounded-xl bg-surface p-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#fb923c] to-[#e11d48] text-white">
                <Icon size={18} aria-hidden />
              </span>
              <span>
                <span className="block text-sm font-bold">{title}</span>
                <span className="block text-[13px] text-fg-muted">{body}</span>
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-fg-muted">Everything that was free before Blaze stays free.</p>
      </SettingsCard>

      <StokesCard status={status} busy={busy} run={run} onStatus={set} />
      <GiftsCard status={status} prices={prices} busy={busy} onBuy={buy} run={run} onStatus={set} />
    </div>
  );
}

function Hero({ status }: { status: BlazeStatusDTO }) {
  const active = !!status.until;
  const months = status.since ? blazeMonths(status.since) : 0;
  return (
    <section className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-[#f97316] via-[#e11d48] to-[#7c3aed] p-6 text-white shadow-pop">
      <Fire size={160} aria-hidden className="pointer-events-none absolute -right-6 -bottom-8 opacity-15" />
      <div className="relative">
        <p className="flex items-center gap-2 text-sm font-semibold tracking-wide text-white/85 uppercase">
          <Fire size={16} aria-hidden /> {APP_NAME}
        </p>
        <h2 className="mt-1 font-display text-3xl font-extrabold tracking-tight">Blaze</h2>
        {active ? (
          <p className="mt-2 max-w-md text-sm text-white/90">
            {months >= 1 ? `Your Blaze has been burning for ${months} month${months === 1 ? '' : 's'}.` : 'Your Blaze is burning.'} Thanks for keeping{' '}
            {APP_NAME} going.
          </p>
        ) : (
          <p className="mt-2 max-w-md text-sm text-white/90">
            Emoji and sounds from every server, server profiles, HD streaming, bigger uploads and two Stokes to light up your favourite servers.
          </p>
        )}
      </div>
    </section>
  );
}

function Plans({ status, prices, busy, onBuy }: { status: BlazeStatusDTO; prices: Partial<Record<BlazeCheckoutKind, LocalPrice>>; busy: string | null; onBuy: (k: BlazeCheckoutKind) => void }) {
  if (!status.onSale) {
    return (
      <SettingsCard title="Coming soon">
        <p className="text-sm text-fg-soft">{BLAZE_NAME} isn&apos;t on sale yet. Keep an eye on #updates.</p>
      </SettingsCard>
    );
  }
  const saving = yearlySaving(prices);
  const plans: Array<{ kind: 'monthly' | 'yearly'; title: string; note: string }> = [
    { kind: 'monthly', title: 'Monthly', note: 'per month' },
    { kind: 'yearly', title: 'Yearly', note: saving && saving >= 5 ? `per year · save ${saving}%` : 'per year' },
  ];
  return (
    <SettingsCard title={`Get ${BLAZE_NAME}`} description="Cancel any time: Blaze stays on until the end of what you've paid for.">
      <div className="grid gap-3 sm:grid-cols-2">
        {plans.map((plan) => (
          <div key={plan.kind} className={cn('flex flex-col rounded-xl border bg-surface p-4', plan.kind === 'yearly' ? 'border-[#f97316]' : 'border-line')}>
            <span className="text-sm font-bold">{plan.title}</span>
            <span className="mt-1 font-display text-2xl font-extrabold">{prices[plan.kind]?.text ?? '—'}</span>
            <span className="text-xs text-fg-muted">{plan.note}</span>
            <Button className="mt-4" block loading={busy === plan.kind} onClick={() => onBuy(plan.kind)}>
              Subscribe
            </Button>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-fg-muted">
        Payments are handled by Paddle, our reseller, who also collects VAT. By subscribing you agree to the{' '}
        <a href="/terms#blaze" target="_blank" rel="noopener" className="text-link hover:underline">
          Blaze terms
        </a>
        . You can cancel within 14 days for a full refund.
      </p>
    </SettingsCard>
  );
}

type Run = (key: string, action: () => Promise<void>) => Promise<void>;

function MyBlaze({ status, busy, run, onStatus }: { status: BlazeStatusDTO; busy: string | null; run: Run; onStatus: (s: BlazeStatusDTO) => void }) {
  const sub = status.subscription;
  return (
    <SettingsCard title="Your Blaze">
      {sub ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm">
            <span className="font-bold">{PLAN_LABEL[sub.plan]} plan.</span>{' '}
            {sub.endsAt ? <>Cancelled: Blaze stays on until {formatDay(sub.endsAt)}.</> : sub.renewsAt ? <>Renews on {formatDay(sub.renewsAt)}.</> : null}
          </p>
          {sub.status === 'past_due' && (
            <p className="rounded-lg bg-warning/15 p-3 text-sm text-warning">
              Your last payment didn&apos;t go through. Update your card in Manage subscription to keep Blaze.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              icon={<ExternalLink size={16} />}
              loading={busy === 'manage'}
              onClick={() =>
                run('manage', async () => {
                  // Opened before the request, so pop-up blockers allow it.
                  const tab = window.open('', '_blank');
                  try {
                    const { url } = await blazeApi.manage();
                    if (tab) {
                      tab.opener = null;
                      tab.location.href = url;
                    } else window.location.href = url;
                  } catch (err) {
                    tab?.close();
                    throw err;
                  }
                })
              }
            >
              Manage subscription
            </Button>
            {sub.endsAt && (
              <Button loading={busy === 'keep'} onClick={() => run('keep', async () => onStatus(await blazeApi.keep()))}>
                Keep my subscription
              </Button>
            )}
          </div>
          <p className="text-xs text-fg-muted">Change your card, see receipts, switch plans or cancel on Paddle&apos;s page.</p>
        </div>
      ) : (
        <p className="text-sm">
          You have free Blaze until <span className="font-bold">{formatDay(status.until!)}</span>.
          {status.onSale && ' Subscribe any time to keep it going after that.'}
        </p>
      )}
      {sub && status.freeUntil && <p className="mt-3 text-xs text-fg-muted">You also have free Blaze until {formatDay(status.freeUntil)}.</p>}
    </SettingsCard>
  );
}

function FounderCard({ status, busy, onClaim }: { status: BlazeStatusDTO; busy: boolean; onClaim: () => void }) {
  const { founder } = status;
  return (
    <SettingsCard>
      <div className="flex items-start gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#fef3c7] via-[#fb923c] to-[#9a3412] text-white">
          <Campfire size={26} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-bold">You&apos;re a Founder</h3>
          <p className="mt-1 text-sm text-fg-muted">
            {founder.claimedAt
              ? `You took your free months on ${formatDay(founder.claimedAt)}. Thanks for being here early.`
              : founder.canClaim
                ? `Thanks for joining ${APP_NAME} early. Your ${FOUNDER_FREE_DAYS / 30} free months of Blaze are ready.`
                : `Thanks for joining ${APP_NAME} early. You'll get ${FOUNDER_FREE_DAYS / 30} months of Blaze free when it launches.`}
          </p>
          {founder.canClaim && (
            <Button className="mt-3" loading={busy} icon={<Fire size={16} />} onClick={onClaim}>
              Claim {FOUNDER_FREE_DAYS / 30} free months
            </Button>
          )}
        </div>
      </div>
    </SettingsCard>
  );
}

function StokesCard({ status, busy, run, onStatus }: { status: BlazeStatusDTO; busy: string | null; run: Run; onStatus: (s: BlazeStatusDTO) => void }) {
  return (
    <SettingsCard
      title="Stokes"
      description={
        status.until
          ? `You've used ${status.stokes.length} of your ${status.stokesTotal}. To use one, open a server's menu and choose Stoke this server.`
          : `${BLAZE_NAME} comes with ${STOKES_PER_BLAZE} Stokes. Stokes raise a server's level for everyone in it.`
      }
    >
      {status.stokes.length > 0 && (
        <ul className="mb-4 flex flex-col gap-2">
          {status.stokes.map((stoke) => (
            <li key={stoke.id} className="flex items-center gap-3 rounded-xl bg-surface p-2.5">
              <ServerIcon server={{ id: stoke.serverId, name: stoke.serverName, iconUrl: stoke.serverIconUrl }} size={32} />
              <span className="min-w-0 flex-1 truncate text-sm font-semibold">{stoke.serverName}</span>
              <Button
                size="sm"
                variant="ghost"
                loading={busy === stoke.id}
                onClick={() => run(stoke.id, async () => onStatus(await blazeApi.removeStoke(stoke.id)))}
              >
                Take back
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid gap-2 sm:grid-cols-3">
        {STOKE_LEVELS.map((level) => (
          <div key={level.level} className="rounded-xl bg-surface p-3">
            <p className="flex items-center gap-1.5 text-sm font-bold">
              <Stoke size={14} aria-hidden className="text-[#f97316]" /> {level.name}
            </p>
            <p className="text-xs text-fg-muted">{level.stokes} Stokes</p>
            <ul className="mt-2 flex flex-col gap-0.5 text-xs text-fg-soft">
              <li>{level.emojiSlots} emoji</li>
              <li>{level.soundSlots} soundboard sounds</li>
              {level.uploadBytes > 0 && <li>{level.uploadBytes / 1024 / 1024} MB uploads for everyone</li>}
              <li className="flex items-center gap-1">
                <ImagePlus size={12} aria-hidden /> Server banner
              </li>
            </ul>
          </div>
        ))}
      </div>
    </SettingsCard>
  );
}

function GiftsCard({
  status,
  prices,
  busy,
  onBuy,
  run,
  onStatus,
}: {
  status: BlazeStatusDTO;
  prices: Partial<Record<BlazeCheckoutKind, LocalPrice>>;
  busy: string | null;
  onBuy: (k: BlazeCheckoutKind) => void;
  run: Run;
  onStatus: (s: BlazeStatusDTO) => void;
}) {
  const [code, setCode] = useState('');
  const giftOptions = (['gift_month', 'gift_year'] as const).filter((k) => status.checkout?.prices[k]);
  const subscribed = !!status.subscription && !status.subscription.endsAt;

  const redeem = (e: FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    void run('redeem', async () => {
      onStatus(await blazeApi.redeem(code.trim()));
      setCode('');
      toast.success(`Gift redeemed. Enjoy ${BLAZE_NAME}!`);
    });
  };

  return (
    <SettingsCard title="Gifts" description={`Give someone ${BLAZE_NAME}: you get a link to send them.`}>
      {status.onSale && giftOptions.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {giftOptions.map((kind) => (
            <Button key={kind} variant="secondary" icon={<Gift size={16} />} loading={busy === kind} onClick={() => onBuy(kind)}>
              Gift {kind === 'gift_month' ? '1 month' : '1 year'}
              {prices[kind] && <span className="text-fg-muted">· {prices[kind].text}</span>}
            </Button>
          ))}
        </div>
      )}
      {status.gifts.length > 0 && (
        <ul className="mb-4 flex flex-col gap-2">
          {status.gifts.map((gift) => {
            const link = `${window.location.origin}/gift/${gift.code}`;
            return (
              <li key={gift.code} className="flex items-center gap-3 rounded-xl bg-surface p-2.5">
                <Gift size={18} aria-hidden className="shrink-0 text-[#e11d48]" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">
                    {giftLength(gift.months)} of Blaze
                  </span>
                  <span className="block text-xs text-fg-muted">
                    {gift.revoked ? 'Refunded' : gift.redeemed ? 'Redeemed' : `Bought ${formatDay(gift.createdAt)} · not used yet`}
                  </span>
                </span>
                {!gift.redeemed && !gift.revoked && (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Copy size={14} />}
                    onClick={async () => ((await copyText(link)) ? toast.success('Gift link copied.') : toast.error('Couldn’t copy the link.'))}
                  >
                    Copy link
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <form onSubmit={redeem} className="flex gap-2">
        <Input aria-label="Gift code" placeholder="Got a gift code? Enter it here" value={code} onChange={(e) => setCode(e.target.value)} maxLength={40} className="flex-1" />
        <Button type="submit" loading={busy === 'redeem'} disabled={!code.trim()}>
          Redeem
        </Button>
      </form>
      {subscribed && <p className="mt-2 text-xs text-fg-muted">Free time from a gift runs alongside your subscription.</p>}
    </SettingsCard>
  );
}

/** A small "Blaze" pill, for things that need it. */
export function BlazeTag({ className, children = 'Blaze' }: { className?: string; children?: ReactNode }) {
  return (
    <span className={cn('inline-flex items-center gap-0.5 rounded-full bg-gradient-to-r from-[#f97316] to-[#e11d48] px-1.5 py-px text-[10px] font-bold tracking-wide text-white uppercase', className)}>
      <Fire size={10} aria-hidden /> {children}
    </span>
  );
}
